<script setup lang="ts">
import { computed } from "vue";

import { formatClock } from "@/lib/focusSession";

/**
 * Self-regulated break screen (PE-8.5).
 *
 * The break is *offered*, never imposed: a 2025 study (PMC12292963) found
 * rigid Pomodoro breaks increased fatigue and decreased motivation relative to
 * self-regulated break-taking. Both Skip and Resume are therefore always
 * available and neither is de-emphasised — nothing here is a lockout.
 *
 * Copy carries no urgency and no loss aversion (PRD §8.3 bans dark patterns,
 * and the cohort skews under 18): no countdown pressure, no "hurry back", no
 * warning about what the student loses by resting.
 *
 * Purely presentational. The container owns the tick and auto-resumes at zero;
 * this component renders the countdown it is handed. The clock comes from
 * `formatClock` so the focus bar, this ring and the mock timer cannot drift
 * apart.
 */

const props = defineProps<{
  secondsLeft: number;
  totalSeconds: number;
}>();

defineEmits<{ skip: []; resume: [] }>();

// A 0–100 dasharray makes the remaining fraction the dash length directly, so
// the ring needs no circumference maths (same trick as AttainmentRing).
const RADIUS = 100 / (2 * Math.PI);
const DIAMETER = RADIUS * 2;
const BOX = DIAMETER + 4;

const remaining = computed(() => {
  if (props.totalSeconds <= 0) return 0;
  return Math.min(1, Math.max(0, props.secondsLeft / props.totalSeconds));
});

const clock = computed(() => formatClock(props.secondsLeft));
</script>

<template>
  <div class="break-screen rl-anim-pop">
    <div class="ring" :style="{ width: '104px', height: '104px' }">
      <svg
        :viewBox="`0 0 ${BOX} ${BOX}`"
        width="104"
        height="104"
        aria-hidden="true"
      >
        <g :transform="`translate(${BOX / 2} ${BOX / 2}) rotate(-90)`">
          <circle class="track" :r="RADIUS" fill="none" stroke-width="2.6" />
          <circle
            class="fill"
            :r="RADIUS"
            fill="none"
            stroke-width="2.6"
            stroke-linecap="round"
            :stroke-dasharray="`${remaining * 100} 100`"
          />
        </g>
      </svg>
      <!-- role="timer" is a live region whose default politeness is off, so a
           screen reader reads the remaining time on demand rather than
           announcing every second. The label carries the value because an
           aria-label would otherwise hide the text content. -->
      <span
        class="clock"
        role="timer"
        :aria-label="`${clock} left in this break`"
        >{{ clock }}</span
      >
    </div>

    <h2 class="title">Take a breather</h2>
    <p class="body">
      Stretch, look away from the screen, rest your eyes. Your session picks up
      right where you left it.
    </p>

    <div class="actions">
      <button type="button" class="secondary" @click="$emit('skip')">
        Skip break
      </button>
      <button type="button" class="primary" @click="$emit('resume')">
        Resume now
      </button>
    </div>
  </div>
</template>

<style scoped>
.break-screen {
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  text-align: center;
}

.ring {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--sp-5);
}

.track {
  stroke: var(--rl-bg-muted);
}

.fill {
  stroke: var(--rl-green-500);
  transition: stroke-dasharray 900ms linear;
}

.clock {
  position: absolute;
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-xl);
  font-weight: 600;
  letter-spacing: -0.01em;
}

.title {
  margin: 0 0 var(--sp-2);
  font-size: var(--rl-text-xl);
  font-weight: 600;
  letter-spacing: -0.02em;
}

.body {
  margin: 0 auto var(--sp-5);
  max-width: 280px;
  font-size: var(--rl-text-base);
  color: var(--rl-fg-3);
  line-height: 1.5;
}

.actions {
  display: flex;
  gap: var(--sp-2);
  justify-content: center;
}

@media (prefers-reduced-motion: reduce) {
  .fill {
    transition: none;
  }
}
</style>
